import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  RetryEntityResponse,
  SyncEntityKind,
  SyncJob,
  SyncJobKind,
  SyncJobStatus,
  SyncJobsResponse,
  SyncOverview,
  SyncRun,
  SyncRunKind,
  SyncRunsResponse,
} from "@zeiterfassung/shared";
import { get, post } from "../client";
import { queryKeys } from "./keys";

export function useSyncOverview(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.syncOverview,
    queryFn: () => get<SyncOverview>("/api/admin/sync/overview"),
    refetchInterval: 30_000,
    enabled: options.enabled,
  });
}

export function useSyncRuns(params: { kind?: SyncRunKind; limit?: number } = {}) {
  const query = new URLSearchParams();
  if (params.kind) query.set("kind", params.kind);
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return useQuery({
    queryKey: queryKeys.syncRuns(params),
    queryFn: () => get<SyncRunsResponse>(`/api/admin/sync/runs${suffix ? `?${suffix}` : ""}`),
    refetchInterval: 30_000,
  });
}

export function useRunSync() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (kind: SyncRunKind) => post<SyncRun>(`/api/admin/sync/run/${kind}`),
    onSettled: () => client.invalidateQueries({ queryKey: ["sync"] }),
  });
}

// Outbound jobs (B1): queue and failures of the sync page, retry and resume.
export function useSyncJobs(
  params: {
    status?: SyncJobStatus;
    kind?: SyncJobKind;
    since?: string;
    lastDays?: number;
    limit?: number;
  } = {},
  options: { enabled?: boolean } = {},
) {
  const query = new URLSearchParams();
  if (params.status) query.set("status", params.status);
  if (params.kind) query.set("kind", params.kind);
  if (params.since) query.set("since", params.since);
  if (params.lastDays) query.set("lastDays", String(params.lastDays));
  if (params.limit) query.set("limit", String(params.limit));
  const suffix = query.toString();
  return useQuery({
    queryKey: queryKeys.syncJobs(params),
    queryFn: () => get<SyncJobsResponse>(`/api/admin/sync/jobs${suffix ? `?${suffix}` : ""}`),
    refetchInterval: 30_000,
    enabled: options.enabled,
  });
}

function useInvalidateSync() {
  const client = useQueryClient();
  return () => {
    client.invalidateQueries({ queryKey: ["sync"] });
    client.invalidateQueries({ queryKey: queryKeys.dispatch });
    client.invalidateQueries({ queryKey: queryKeys.timeEntries });
  };
}

export function useRetrySyncJob() {
  const invalidate = useInvalidateSync();
  return useMutation({
    mutationFn: (id: string) => post<SyncJob>(`/api/admin/sync/jobs/${id}/retry`),
    onSuccess: invalidate,
  });
}

export function useRetryEntitySync() {
  const invalidate = useInvalidateSync();
  return useMutation({
    mutationFn: (input: { entity: SyncEntityKind; id: string }) =>
      post<RetryEntityResponse>(`/api/admin/sync/entities/${input.entity}/${input.id}/retry`),
    onSuccess: invalidate,
  });
}

export function useResumeSync() {
  const invalidate = useInvalidateSync();
  return useMutation({
    mutationFn: () => post<{ ok: true }>("/api/admin/sync/resume"),
    onSuccess: invalidate,
  });
}

// Whether the overview has something a dispatcher should look at: a failed last run, failed
// jobs or a paused queue. Drives the dispatcher's "Sync" navigation entry.
export function syncHasProblems(overview: SyncOverview | undefined): boolean {
  if (!overview) return false;
  return (
    overview.paused !== null ||
    overview.pausedUntil !== null ||
    overview.failedJobs > 0 ||
    overview.kinds.some((k) => k.lastRun?.status === "error")
  );
}
